'use client';

import React from 'react';
import dynamic from 'next/dynamic';
// Lazily loaded (own chunk): katex is ~70KB gz and most pages have no math.
const KatexMath = dynamic(() => import('./blocks/KatexMath'));
import {
  Content,
  Parent,
  Text,
  Heading,
  Emphasis,
  Strong,
  Paragraph,
  Link,
  FootnoteReference,
  List as MdList,
  ListItem as MdListItem,
  Blockquote,
} from 'mdast';
import { ChunkDocContext } from '@/lib/parseMarkdown';
import type { HtmlTableNode } from '@/lib/parseGdocTableHtml';
import Tooltip from '@/ui/Tooltip';
import FootnoteModal from './FootnoteModal';
import { Redo2 } from 'lucide-react';
import slugify from 'slugify';
import { panelData } from '@/data/panelData';
import TabBoxes from './blocks/TabBoxes';
import TriangleMarker from './blocks/TriangleMarker';
const GameEmbed = dynamic(() => import('./blocks/GameEmbed'));
import { BranchpointBox } from './blocks/BranchpointBox';
import { syncDetailsHash, openDetailsIfHashTarget, footnotesLinkWithOrigin } from './blocks/detailsHashSync';
import { getLinkTooltipMarkdown, linkPreviewHoverId, LinkTooltip } from './blocks/linkTooltips';
import { PlanPrinciplesBox } from './blocks/PlanPrinciplesBox';
import { TwoBoxes2029and2030 } from './blocks/TwoBoxes2029and2030';
const VerificationPlanTimeline = dynamic(() => import('./VerificationPlanTimeline'));
const IncrementalistWishlistDiagram = dynamic(() => import('@/app/figures-code/incrementalist-wishlist/IncrementalistWishlistDiagram'));
const PolicyScatterChart = dynamic(() => import('@/app/figures-code/policy-scatter/PolicyScatterChart'));

const GPTScale = dynamic(() => import('./embeds/GPTScale'));
const DeploymentCurves = dynamic(() => import('./embeds/DeploymentCurves'));
const KeyMetrics2026 = dynamic(() => import('./embeds/KeyMetrics2026'));
const ChinaComputeCentralization = dynamic(() => import('./embeds/ChinaComputeCentralization'));
const ComputeAllocation2024v2027 = dynamic(() => import('./embeds/ComputeAllocation2024v2027'));
const TakeoffMethod = dynamic(() => import('./embeds/TakeoffMethod'));
const TimelinesMethod = dynamic(() => import('./embeds/TimelinesMethod'));
const WeightsSecurityTimeline = dynamic(() => import('./embeds/WeightsSecurityTimeline'));
const SecretsSecurityTimeline = dynamic(() => import('./embeds/SecretsSecurityTimeline'));
import { unescapeMarkdownForLaTeX } from '@/lib/utils';
import PublicationDate from './blocks/PublicationDate';
import IntroLinkCards from './blocks/IntroLinkCards';
import ZoomableImage from './blocks/ZoomableImage';
import Image from 'next/image';
import optimizedImages from '@/lib/optimizedImages';
import { ErrorBoundary } from 'react-error-boundary';
import { type PlanId } from '@/lib/branchTypes';
import PerspectiveChoiceContent from './blocks/perspective-choice/PerspectiveChoiceContent';
import { FIGURES_DIRECTIVES, FIGURE_DIRECTIVES_WITH_INLINE_CAPTION } from '@/app/figures-code/markdownDirectives';
import { FigureCaptionContext } from '@/app/figures-code/figureStyle';
const TimelineQuestionsDiagram = dynamic(() => import('@/app/figures-code/timeline-questions/TimelineQuestionsDiagram'));

// Story content (generated from the gdoc) embeds some figures as static PNGs
// under /PlanATabs/. Where an interactive figure component now exists, render
// it instead of the image — keyed by the exact image URL so the substitution
// survives content re-imports without gdoc edits.
const IMAGE_FIGURE_REPLACEMENTS: Record<string, string> = {
  // NOTE: gdoc re-imports renumber these exported images (a July 2026 import
  // rotated the whole t2/t3 series by one). After every content import, run
  // `node scripts/checkFigureImageReplacements.mjs` — it verifies each mapped
  // URL still holds the chart this table says it does, by content hash.
  // (July 7 2026 import: t2 series reshuffled — CovertComputeSankey moved to
  // t2_image1, OpenSourceSpectrum now at t2_image6; the EpistemicsInterventions
  // png left the doc and the t3 tail shifted, putting IncomeDistribution at
  // t3_image5. t2_image8-10 / t3_image6-8 on disk are unreferenced leftovers.)
  // (July 8 2026 sync: t3_image2/t3_image3 swapped places; the AlignmentOverTime
  // chart left the doc, shifting SafetyCaseBuildingBlocks to t4_image1 and
  // leaving t4_image2 an unreferenced duplicate; t2_image6 now holds the
  // detection-adjusted covert-forecast chart, which has no interactive
  // directive — its entry is dropped so the exported image renders as-is.)
  '/PlanATabs/t0_image2.png': 'CompanyRevenueLinear',
  '/PlanATabs/t0_image3.png': 'TimeHorizon80p',
  // Plan D stub's takeoff figure: the doc exports the aifuturesmodel.com
  // three-panel screenshot; render the racing-takeoff SVG chart instead.
  '/PlanATabs/t1_image1.png': 'RacingTakeoffUplift',
  '/PlanATabs/t2_image1.png': 'CovertComputeSankey',
  '/PlanATabs/t3_image1.png': 'LaborIncomeShare',
  '/PlanATabs/t3_image2.png': 'LaborPopulationLinear',
  '/PlanATabs/t3_image3.png': 'ComputeGrowth',
  '/PlanATabs/t3_image5.png': 'IncomeDistribution',
  '/PlanATabs/t4_image1.png': 'SafetyCaseBuildingBlocks',
  '/PlanAPerspectives/insider_t0_image1.png': 'CompanyRevenueLinear',
  '/PlanAPerspectives/insider_t0_image2.png': 'TimeHorizon80p',
  '/supplements/transparency-plan/image3.png': 'TransparencyBenefits',
  // (The covert doc's chip-lifecycle and tracing-tree figures need no image
  // mappings: the doc now embeds them as :::ChipLifecycle /
  // :::ChipTracingTree directives — July 9 2026.)
};

// Art for the three "kinds of territory" in 2036 (2036-life-after-work.md),
// keyed by the `kind` attribute of a `:::territory{kind=…}` directive. The
// directive pairs each image with its description (rendered as markdown
// children, so footnotes still work). Files live outside /PlanATabs/ so the
// gdoc image pipeline doesn't overwrite them.
const TERRITORY_IMAGES: Record<string, { src: string; full: string; alt: string }> = {
  sez: { src: '/territory/sez.jpg', full: '/territory/sez-full.jpg', alt: 'An industrial special economic zone: a vast desert factory-and-strip-mine complex' },
  arcology: { src: '/territory/arcology.jpg', full: '/territory/arcology-full.jpg', alt: 'An arcology: green skyscraper-mall complexes surrounded by nature' },
  preserve: { src: '/territory/nature-preserve.jpg', full: '/territory/nature-preserve-full.jpg', alt: 'A historic & nature preserve: a leafy suburban street, little changed' },
};

// Wrapper for figures dropped into narrative prose (shared by the container
// directive branch and the image-substitution branch): neutralize the
// figure-card outer padding and force sane heading line-height.
const FIGURE_IN_PROSE_CLASSES = [
  'my-6',
  '[&>div]:!px-0 [&>div]:!py-0 [&>div]:!max-w-none',
  '[&_h1]:!leading-snug [&_h2]:!leading-snug [&_p]:!leading-snug',
  '[&_h1]:!mb-1 [&_h2]:!mb-1',
].join(' ');



// The branch-root chunks pace their timelines with hand-inserted
// `:::dateMarker` blocks (see scripts/planA/preserveStubDateMarkers.ts).
// A marker anchored to a paragraph inside a multi-paragraph quote splits
// that quote into separate mdast blockquotes, which render as separate
// quote blocks (blockquote styling in globals.css). Re-join such runs
// into one blockquote, moving the marker nodes inside it: the marker
// renders as an invisible zero-height anchor div, so the timeline pacing
// is unchanged while the quote reads as one block.
function mergeBlockquotesSplitByDateMarkers(nodes: Content[]): Content[] {
  const isDateMarker = (n: Content): boolean => {
    const c = n as { type?: string; name?: string };
    return c.type === 'containerDirective' && c.name === 'dateMarker';
  };
  const out: Content[] = [];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.type !== 'blockquote') {
      out.push(node);
      continue;
    }
    // Content[] rather than Blockquote['children']: the merged quote holds
    // dateMarker containerDirectives, which mdast's Blockquote type excludes.
    const children: Content[] = [...node.children];
    let end = i; // last node consumed into this blockquote
    let scan = i + 1;
    while (true) {
      const markers: Content[] = [];
      while (scan < nodes.length && isDateMarker(nodes[scan])) {
        markers.push(nodes[scan]);
        scan++;
      }
      // Markers not followed by another blockquote stay outside the box.
      if (markers.length === 0 || scan >= nodes.length) break;
      const following = nodes[scan];
      if (following.type !== 'blockquote') break;
      children.push(...markers, ...following.children);
      end = scan;
      scan++;
    }
    if (end === i) {
      out.push(node);
    } else {
      out.push({ ...node, children } as Content);
    }
    i = end;
  }
  return out;
}

export type FootnoteHandler = React.FC<{ footnoteReference: FootnoteReference, docContext?: ChunkDocContext }>

interface MarkdownProps {
  nodes: Content[];
  docContext?: ChunkDocContext;
  ending?: 'race' | 'slowdown'
  FootnoteHandler?: FootnoteHandler
  baseFontSize?: string
  // Use descendant variants (`[&_p]:text-xs`): tufte.css targets
  // descendants, so a wrapper font-size alone won't override them.
  className?: string
  onPlanHover?: (planId: PlanId | null) => void
  onBranchHover?: (targetId: string | null) => void
}

// Split a paragraph's inline children into table cells on '|';
// non-text nodes (inlineMath, strong, link) stay whole.
function splitInlineByPipe(children: any[]): any[][] {
  const cells: any[][] = [[]]
  for (const node of children) {
    if (node && node.type === 'text' && typeof node.value === 'string') {
      const parts = node.value.split('|')
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) cells.push([])
        if (parts[i] !== '') {
          cells[cells.length - 1].push({ ...node, value: parts[i] })
        }
      }
    } else {
      cells[cells.length - 1].push(node)
    }
  }
  const isEmpty = (cell: any[]) =>
    cell.length === 0 ||
    cell.every((n) => n.type === 'text' && (n.value as string).trim() === '')
  if (cells.length > 0 && isEmpty(cells[0])) cells.shift()
  if (cells.length > 0 && isEmpty(cells[cells.length - 1])) cells.pop()
  return cells.map((cell) => {
    const out = [...cell]
    if (out.length > 0 && out[0].type === 'text') {
      out[0] = { ...out[0], value: (out[0].value as string).replace(/^\s+/, '') }
      if (out[0].value === '') out.shift()
    }
    if (out.length > 0 && out[out.length - 1].type === 'text') {
      const last = out[out.length - 1]
      out[out.length - 1] = { ...last, value: (last.value as string).replace(/\s+$/, '') }
      if (out[out.length - 1].value === '') out.pop()
    }
    return out
  })
}

function inlineText(nodes: any[]): string {
  return nodes
    .map((n) => (n && n.type === 'text' ? n.value : ''))
    .join('')
}

function getTextFromNode(node: Content): string {
  if (node.type === 'text') {
    return (node as Text).value;
  }
  if (("children" in node) && node.children && Array.isArray(node.children)) {
    return node.children.map(getTextFromNode).join(' ');
  }
  return '';
}

export function FootnoteRef({
  footnoteReference,
  docContext,
}: {
  footnoteReference: FootnoteReference;
  docContext?: ChunkDocContext;
}) {
  const prefix = docContext?.prefix;
  const identifier = prefix ? `${prefix}-${footnoteReference.identifier}` : footnoteReference.identifier;
  const definition = docContext?.footnotes.find((f) => f.identifier === footnoteReference.identifier);
  const [modalOpen, setModalOpen] = React.useState(false);
  // Same breakpoint the Tooltip itself uses for its touch behavior. On
  // mobile viewports the tooltip is deactivated entirely — a tap opens the
  // modal, and without this the Tooltip's own tap-to-open would stack a
  // tooltip behind the modal on the same tap.
  const [isMobileViewport, setIsMobileViewport] = React.useState(false);
  React.useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobileViewport(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  if (!definition) {
    return (
      <sup id={`footnote-${identifier}`}>
        {/* Rewrite the href on the client so `/footnotes` carries the page we
            came from (see footnotesLinkWithOrigin); the `↩` there returns here. */}
        <a
          href={`/footnotes#footnote-${identifier}`}
          onClick={(e) => {
            e.currentTarget.href = footnotesLinkWithOrigin(`footnote-${identifier}`);
          }}
        >
          [{footnoteReference.identifier}]
        </a>
      </sup>
    );
  }

  // A serif card led by a small red footnote number matching the in-text
  // reference. The card renders in a portal at <body>, so text-[var(--accent)]
  // must resolve at the root (Playbook sets --accent there), not from the
  // article's scope.
  const tooltipContent = (
    // Background/border sit on this non-scrolling wrapper: Safari's
    // rubber-band overscroll drags the scroller's own paint layer, so a
    // background on the scrolling element flashes transparent at the bounce.
    <div className="w-[470px] max-w-full bg-vivid-background border-[1.5px] border-black rounded-md overflow-hidden">
      <div className="markdown-content flex items-start gap-[8px] max-h-[70vh] overflow-y-auto px-3 py-4 font-[et-book,Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif] text-black text-[1.2rem] leading-[1.4] [&_p]:leading-[1.4] [&_li]:leading-[1.4] [&_p]:my-[0.5em] [&_p]:text-[1.2rem] [&_ol]:text-[1.2rem] [&_ol]:m-0 [&_ul]:text-[1.2rem] [&_ul]:m-0 [&_li]:text-[1.2rem] [&_li]:m-0">
        <sup className="shrink-0 text-sm mt-2 opacity-70">#{footnoteReference.identifier}</sup>
        <div className="min-w-0 [&>p:first-of-type]:mt-0 [&>p:last-of-type]:mb-0">
          <Markdown nodes={definition.children} docContext={docContext} FootnoteHandler={FootnoteRef} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Tooltip content={tooltipContent} placement="bottom-start" interactive maxWidth={470} disableShadow deactivate={isMobileViewport}>
        <sup className="relative text-[var(--accent)] cursor-pointer" id={`footnote-${identifier}`}>
          <a
            href={`/footnotes#footnote-${identifier}`}
            className="text-[var(--accent)] text-xs no-underline"
            onClick={(e) => {
              // Modified clicks (new tab/window) keep the `/footnotes`
              // navigation, tagged with the page we came from so its `↩`
              // returns here (see footnotesLinkWithOrigin).
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                e.currentTarget.href = footnotesLinkWithOrigin(`footnote-${identifier}`);
                return;
              }
              e.preventDefault();
              setModalOpen(true);
            }}
          >
            {footnoteReference.identifier}
          </a>
        </sup>
      </Tooltip>
      {modalOpen && (
        <FootnoteModal
          identifier={footnoteReference.identifier}
          anchorId={`footnote-${identifier}`}
          onClose={() => setModalOpen(false)}
        >
          <Markdown nodes={definition.children} docContext={docContext} FootnoteHandler={FootnoteRef} />
        </FootnoteModal>
      )}
    </>
  );
}

// ─── :::recsNested rendering ────────────────────────────────────────
// Depth layers: 0 = category, 1 = sub-rec, 2+ = explanation.

// `!` overrides because supplement prose sets font on descendants.
const RECS_NESTED_HEAD_CLS: string[] = [
  '!text-[1.55rem] font-bold !leading-tight text-black',     // depth 0 (category) — biggest
  '!text-[1.2rem] font-bold !leading-snug text-black',       // depth 1 (sub-rec) — a bit bigger
  '!text-[1.05rem] font-semibold !leading-snug text-black/85', // depth 2+
]
const RECS_NESTED_EXPL_CLS = 'mt-1 !text-[1rem] !leading-snug text-black/55'

function RecsNestedNodes({ list, depth, docContext, FootnoteHandler }: {
  list: any; depth: number; docContext?: ChunkDocContext; FootnoteHandler: FootnoteHandler
}) {
  const items: any[] = list?.children ?? []
  const containerCls =
    depth === 0 ? 'flex flex-col gap-7'
    : depth === 1 ? 'mt-3 flex flex-col gap-4'
    : 'mt-2 flex flex-col gap-2'
  const headCls = RECS_NESTED_HEAD_CLS[Math.min(depth, RECS_NESTED_HEAD_CLS.length - 1)]
  return (
    <div className={containerCls}>
      {items.map((item: any, i: number) => {
        const para = (item.children ?? []).find((c: any) => c.type === 'paragraph')
        const childList = (item.children ?? []).find((c: any) => c.type === 'list')
        const kids: any[] = para?.children ?? []
        const recurse = childList && (
          <RecsNestedNodes list={childList} depth={depth + 1} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        )
        // Layers 0-1: leading bold = head, trailing text = grey line.
        const isHeadingLayer = depth <= 1
        let body: React.ReactNode
        if (!isHeadingLayer) {
          body = (
            <>
              <div className={RECS_NESTED_EXPL_CLS}>
                <Markdown nodes={kids} docContext={docContext} FootnoteHandler={FootnoteHandler} />
              </div>
              {recurse}
            </>
          )
        } else {
          const hasStrong = kids[0]?.type === 'strong'
          const head = hasStrong ? [kids[0]] : kids
          let tail = hasStrong ? kids.slice(1) : []
          // Trim a leading whitespace/". " node off explanation.
          if (tail.length && tail[0].type === 'text') {
            const trimmed = String(tail[0].value).replace(/^[\s.]+/, '')
            tail = trimmed ? [{ ...tail[0], value: trimmed }, ...tail.slice(1)] : tail.slice(1)
          }
          body = (
            <>
              <div className={headCls}>
                <Markdown nodes={head} docContext={docContext} FootnoteHandler={FootnoteHandler} />
              </div>
              {tail.length > 0 && (
                <div className={RECS_NESTED_EXPL_CLS}>
                  <Markdown nodes={tail} docContext={docContext} FootnoteHandler={FootnoteHandler} />
                </div>
              )}
              {recurse}
            </>
          )
        }
        if (depth === 0) {
          return <div key={i} className="pl-5 border-l-[3px] border-black/80">{body}</div>
        }
        return <div key={i}>{body}</div>
      })}
    </div>
  )
}

function RecsNested({ nodes, docContext, FootnoteHandler }: {
  nodes: any[]; docContext?: ChunkDocContext; FootnoteHandler: FootnoteHandler
}) {
  const list = (nodes ?? []).find((n: any) => n.type === 'list')
  if (!list) return null
  return (
    <div className="my-7">
      <RecsNestedNodes list={list} depth={0} docContext={docContext} FootnoteHandler={FootnoteHandler} />
    </div>
  )
}

export default function Markdown({
  nodes,
  docContext,
  ending,
  FootnoteHandler = FootnoteRef,
  baseFontSize,
  className,
  onPlanHover,
  onBranchHover,
}: MarkdownProps) {
  // A `:::caption` block that directly follows a figure is paired with it:
  // the caption is passed into the figure via FigureCaptionContext and
  // <FigureCaption> renders it between the chart and the title/attribution
  // line, so the attribution ends up BELOW the caption. Only figures that
  // render <FigureCaption> can host the caption inline this way; for any
  // other figure we DON'T pair, so its `:::caption` falls through to the
  // ordinary trailing-block rendering (which is where it went before).
  const inlineCaptionFigure = (n: Content): boolean => {
    const c = n as { type?: string; name?: string; children?: Array<{ type?: string; url?: string }> };
    if (c.type === 'containerDirective' && c.name) return FIGURE_DIRECTIVES_WITH_INLINE_CAPTION.has(c.name);
    if (c.type === 'paragraph' && c.children?.length === 1) {
      const lone = c.children[0];
      const directive = lone?.type === 'image' && lone.url ? IMAGE_FIGURE_REPLACEMENTS[lone.url] : undefined;
      return !!directive && FIGURE_DIRECTIVES_WITH_INLINE_CAPTION.has(directive);
    }
    return false;
  };
  const mergedNodes = mergeBlockquotesSplitByDateMarkers(nodes);
  const blocks: React.ReactNode[] = [];
  for (let index = 0; index < mergedNodes.length; index++) {
    const node = mergedNodes[index];
    const next = index + 1 < mergedNodes.length
      ? (mergedNodes[index + 1] as { type?: string; name?: string; children?: Content[] })
      : null;
    const nodeEl = (
      <Node
        node={node}
        key={index}
        docContext={docContext}
        ending={ending}
        FootnoteHandler={FootnoteHandler}
        onPlanHover={onPlanHover}
        onBranchHover={onBranchHover}
      />
    );
    if (next && next.type === 'containerDirective' && next.name === 'caption' && inlineCaptionFigure(node)) {
      const captionContent = (
        <Markdown nodes={next.children ?? []} docContext={docContext} FootnoteHandler={FootnoteHandler} />
      );
      blocks.push(
        <FigureCaptionContext.Provider key={index} value={captionContent}>
          {nodeEl}
        </FigureCaptionContext.Provider>
      );
      index++; // caption consumed
      continue;
    }
    blocks.push(nodeEl);
  }
  const content = (
    <ErrorBoundary fallback={<></>}>
      {blocks}
    </ErrorBoundary>
  );
  if (baseFontSize || className) {
    return (
      <div
        className={className}
        style={baseFontSize ? { fontSize: baseFontSize } : undefined}
      >
        {content}
      </div>
    );
  }
  return content;
}

// Reads merge attrs from node.data.hProperties if present.
function TableCell({
  node,
  docContext,
  as: Component = 'td',
  FootnoteHandler,
}: {
  node: any;
  docContext?: ChunkDocContext;
  as?: 'td' | 'th';
  FootnoteHandler: ({ footnoteReference, docContext }: { footnoteReference: FootnoteReference, docContext?: ChunkDocContext }) => React.ReactNode;
}) {
  const { colspan, rowspan, ...rest } = node?.data?.hProperties || {};

  let hasSpecialChar = false;
  let nodeValue = '';

  if (node.children &&
    node.children.length === 1 &&
    node.children[0].type === 'text') {
    nodeValue = node.children[0].value.trim();
    hasSpecialChar = nodeValue === '+';

    if (hasSpecialChar) {
      node.children[0].value = '';
    }
  }

  let cellClass = "border border-gray-300 px-2 py-1";

  if (Component === 'th' && !hasSpecialChar) {
    cellClass += " font-semibold text-center";
  }

  return (
    <Component
      colSpan={colspan}
      rowSpan={rowspan}
      {...rest}
      className={cellClass}
    >
      <Markdown nodes={node.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
    </Component>
  );
}

type MarkdownNodeProps = {
  node: Content;
  docContext?: ChunkDocContext;
  ending?: 'slowdown' | 'race';
  FootnoteHandler: (
    { footnoteReference, docContext }: {
      footnoteReference: FootnoteReference;
      docContext?: ChunkDocContext;
    }
  ) => React.ReactNode;
  onPlanHover?: (planId: PlanId | null) => void;
  onBranchHover?: (targetId: string | null) => void;
};

// Quote attributions use a spaced dash before the author name ('"Plans are
// worthless…" - Dwight D. Eisenhower', em/en dash or plain hyphen), and
// browsers treat a dash as a line-break opportunity — so the name can wrap
// away from its dash, stranding the dash at the end of the previous line.
// Glue a spaced dash to whatever follows it: a following space becomes
// NO-BREAK SPACE, and a dash that ends the text node (the name continues in
// a following <em>/<a> node, e.g. "—*Yoshua Bengio*") gets a WORD JOINER so
// the node boundary isn't a break opportunity either. Closed dashes in
// running prose ("must—and will—prevail"), numeric ranges ("2025–2030"),
// and hyphenated words are untouched.
function guardAttributionDashBreaks(value: string): string {
  return value
    .replace(/(^|\s)([—–-])[ \t]+/g, '$1$2\u00A0')
    .replace(/(^|\s)([—–-])$/, '$1$2\u2060');
}

function Node({
  node,
  docContext,
  ending,
  FootnoteHandler,
  onPlanHover,
  onBranchHover,
}: MarkdownNodeProps): React.ReactNode {

  switch (node.type as string) {
    case 'text': {
      const t = node as Text;
      return <React.Fragment>{guardAttributionDashBreaks(t.value)}</React.Fragment>;
    }
    case 'paragraph': {
      const p = node as Paragraph;
      // A paragraph that is exactly one figure-replaced image is hoisted out of
      // the <p> entirely: figure components render block <div>s, which are
      // invalid inside <p> and break hydration.
      const loneChild = p.children.length === 1 ? (p.children[0] as { type?: string; url?: string }) : null;
      const loneFigure = loneChild?.type === 'image' && loneChild.url
        ? IMAGE_FIGURE_REPLACEMENTS[loneChild.url]
        : undefined;
      if (loneFigure && FIGURES_DIRECTIVES[loneFigure]) {
        return (
          <div className={FIGURE_IN_PROSE_CLASSES}>
            {FIGURES_DIRECTIVES[loneFigure]}
          </div>
        );
      }
      return (
        <p>
          <Markdown nodes={p.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </p>
      );
    }
    case 'heading': {
      const h = node as Heading;
      const Tag = `h${h.depth}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const textContent = getTextFromNode(h);
      // A heading that is just a figure-replaced image (a gdoc artifact:
      // an image pasted on a heading-styled line) hoists the figure out of
      // the heading tag entirely, same as the lone-image paragraph case.
      const meaningful = h.children.filter(
        (c) => !(c.type === 'text' && (c as Text).value.trim() === ''),
      );
      const loneHeadingChild = meaningful.length === 1 ? (meaningful[0] as { type?: string; url?: string }) : null;
      const headingFigure = loneHeadingChild?.type === 'image' && loneHeadingChild.url
        ? IMAGE_FIGURE_REPLACEMENTS[loneHeadingChild.url]
        : undefined;
      if (headingFigure && FIGURES_DIRECTIVES[headingFigure]) {
        return (
          <div className={FIGURE_IN_PROSE_CLASSES}>
            {FIGURES_DIRECTIVES[headingFigure]}
          </div>
        );
      }
      const id = slugify(textContent, { lower: true, strict: true });
      return (
        <Tag id={id}>
          {/* Hidden by default; shown only in supplement prose via globals.css. */}
          <a href="#top-toc" className="toc-backlink" aria-label="Back to contents" />
          <Markdown nodes={h.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </Tag>
      );
    }
    case 'strong': {
      const s = node as Strong;
      return (
        <strong>
          <Markdown nodes={s.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </strong>
      );
    }
    case 'emphasis': {
      const e = node as Emphasis;
      return (
        <em>
          <Markdown nodes={e.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </em>
      );
    }
    case 'delete': {
      // GFM ~~strikethrough~~. Without this case it falls into the default
      // <div> wrapper, which is invalid inside <p> and breaks hydration.
      const d = node as Parent;
      return (
        <del>
          <Markdown nodes={d.children as Content[]} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </del>
      );
    }
    case 'blockquote': {
      const bq = node as Blockquote;
      // `ending` is forwarded because merged blockquotes (see
      // mergeBlockquotesSplitByDateMarkers) can carry dateMarker anchors,
      // whose element ids depend on it.
      return (
        <blockquote>
          <Markdown nodes={bq.children} docContext={docContext} ending={ending} FootnoteHandler={FootnoteHandler} />
        </blockquote>
      );
    }
    case 'link': {
      const l = node as Link;
      const isImageLink = /\.(png|jpe?g|gif|webp|svg)$/i.test(l.url);
      const isInternalLink = l.url.startsWith('/') || l.url.startsWith('#');
      // Marker links (used by changelog entries): a link whose entire text is
      // "↗" renders as a swoopy go-to arrow — icon-only, accent red, no
      // underline.
      if (getTextFromNode(l).trim() === '↗') {
        return (
          <a
            href={l.url}
            {...(isInternalLink ? {} : { target: '_blank' })}
            className="!no-underline text-[#8b0000]"
            aria-label="Go to this change on the site"
          >
            <Redo2 className="inline h-[0.9em] w-[0.9em] -translate-y-[0.08em]" strokeWidth={2.2} />
          </a>
        );
      }
      // Links whose URL is registered in linkTooltips.tsx get a hover card
      // rendering a curated markdown file (URL in small type at the bottom).
      // Takes precedence over the link-title preview below.
      const tooltipMarkdown = getLinkTooltipMarkdown(l.url);
      // Tagged for the delegated hover analytics (ui/hoverTracking.tsx);
      // the tooltip contents below carry the same id.
      const hasHoverPreview = isImageLink || !!tooltipMarkdown || !!l.title;
      const anchor = (
        <a
          href={l.url}
          {...(isInternalLink ? {} : { target: '_blank' })}
          {...(hasHoverPreview ? { 'data-track-hover': linkPreviewHoverId(l.url) } : {})}
        >
          <Markdown nodes={l.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </a>
      );
      if (isImageLink) {
        return (
          <Tooltip
            content={<img src={l.url} alt="" data-track-hover={linkPreviewHoverId(l.url)} style={{maxWidth: '100%', display: 'block'}} />}
            placement="top"
            maxWidth={400}
            styleManually
          >
            {anchor}
          </Tooltip>
        );
      }
      if (tooltipMarkdown) {
        return (
          <LinkTooltip url={l.url} markdown={tooltipMarkdown}>
            {anchor}
          </LinkTooltip>
        );
      }
      // A link-title note ([text](url "note")) becomes a hover preview that
      // explains why the linked page is relevant here. mdast exposes it as
      // l.title; links without a title render as the bare anchor above.
      if (l.title) {
        const host = /^https?:\/\//.test(l.url)
          ? new URL(l.url).hostname.replace(/^www\./, '')
          : null;
        return (
          <Tooltip
            content={
              <div data-track-hover={linkPreviewHoverId(l.url)} className="font-sans px-3 py-2 max-w-[340px]">
                <div className="font-mono text-sm font-bold text-black">
                  {getTextFromNode(l)}
                </div>
                {host && <div className="text-xs text-black/50 mb-1">{host}</div>}
                <div className="text-sm text-black/80">{l.title}</div>
              </div>
            }
            placement="top"
            interactive
            maxWidth={360}
          >
            {anchor}
          </Tooltip>
        );
      }
      return anchor;
    }
    case 'image': {
      const image = node as { url: string; alt?: string; title?: string };
      // (Figure substitution for images happens at the paragraph level — see
      // case 'paragraph'. An image mixed inline with other content stays an
      // <img>: figure components render block <div>s, invalid inside <p>.)
      // "nowatermark" titles are a legacy opt-out from the removed corner-badge
      // logic; still swallow the keyword so it doesn't render as a caption.
      const titleIsLegacyNowatermark = image.title?.trim().toLowerCase() === 'nowatermark';
      const captionText = titleIsLegacyNowatermark ? (image.alt || '') : (image.title || image.alt || '');
      const shouldShiftRight = image.url.includes('-inexpandable');
      return (
        <span className="block flex flex-col items-center justify-center mb-4 mt-4 relative">
          <span className="relative">
          {optimizedImages[image.url] ? (
            <span className={`relative max-w-full h-auto ${shouldShiftRight ? 'md:ml-[-8px]' : 'md:ml-[-20px]'} py-4 px-2 ${image.alt && "mix-blend-multiply"}`}>
              <Image
                src={optimizedImages[image.url]}
                alt={image.alt || ""}
                title={image.title || ""}
                className={``}
                loading='lazy'
              />
            </span>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={image.url}
              alt={image.alt || ""}
              title={image.title || ""}
              className={`max-w-full h-auto ${shouldShiftRight ? 'md:ml-[-8px]' : 'md:ml-[-20px]'} py-4 px-2 ${image.alt && "mix-blend-multiply"}`}
            />
          )}
          </span>
          <span className="block text-center text-sm font-mono">{captionText}</span>
        </span>
      );
    }
    case 'footnoteReference': {
      const fr = node as FootnoteReference;
      return <FootnoteHandler footnoteReference={fr} docContext={docContext} />;
    }
    case 'list': {
      const list = node as MdList;
      const children = <Markdown nodes={list.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />;
      return list.ordered ? (
        <ol className="pl-6">{children}</ol>
      ) : (
        <ul className="pl-6">{children}</ul>
      );
    }
    case 'listItem': {
      const li = node as MdListItem;
      return (
        <li className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0 my-1 [&>p+ul]:mt-0 [&>p+ol]:mt-0 [&>p:has(+ul)]:mb-0 [&>p:has(+ol)]:mb-0">
          <Markdown nodes={li.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </li>
      );
    }
    case 'table': {
      const table = node as any;
      if (!table.children || table.children.length === 0) return null;
      const headerRow = table.children[0];
      const bodyRows = table.children.slice(1);
      return (
        <div className="overflow-x-auto">
          <table className="table-auto border-collapse border border-gray-300 my-4 text-[0.8em]">
            <thead className="bg-gray-100">
              <tr>
                {headerRow.children.map((cell: any, cellIndex: number) => (
                  <TableCell
                    key={cellIndex}
                    node={cell}
                    docContext={docContext}
                    as="th"
                    FootnoteHandler={FootnoteHandler}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.children.map((cell: any, cellIndex: number) => (
                    <TableCell
                      key={cellIndex}
                      node={cell}
                      docContext={docContext}
                      as="td"
                      FootnoteHandler={FootnoteHandler}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'tableRow': {
      const row = node as any;
      return (
        <tr>
          {row.children.map((cell: any, i: number) => (
            <TableCell key={i} node={cell} docContext={docContext} as="td" FootnoteHandler={FootnoteHandler} />
          ))}
        </tr>
      );
    }
    case 'tableCell': {
      return <TableCell node={node} docContext={docContext} as="td" FootnoteHandler={FootnoteHandler} />;
    }
    case 'containerDirective': {
      const container = node as any;
      if (container.name === 'dateMarker') {
        const value = container?.children
          .flatMap((child: any) => child.children)
          .map((child: any) => child.value)
          .join('\n');
        const [dateString, dateHeaderString] = value.split('\n').filter((v: string) => v.trim().length > 0);

        const matchingEntry = panelData.find(([date, , dataPointEnding]) => ending ? (date === dateString && ending === dataPointEnding) : (date === dateString && dataPointEnding === undefined));
        // if (!matchingEntry) {
        //   throw new Error(`No panelData entry found for date: ${dateString}`);
        // }

        return (
          <div
            className="scroll-mt-[50vh]"
            id={`${ending || 'narrative'}-${dateString}`}
            data-chart-date={dateString}
            data-chart-date-header={dateHeaderString}
            data-chart-extra={JSON.stringify(matchingEntry)}
            style={{ height: 0 }}
          />
        );
      } else if (container.name === 'legend') {
        const value = container?.children
          .flatMap((child: any) => child.children)
          .map((child: any) => child.value)
          .join('\n');
        const [legendLabelFromBody] = value.split('\n').filter((v: string) => v.trim().length > 0);
        const legendLabel = container.attributes?.label || legendLabelFromBody;
        if (!legendLabel) return null;
        return (
          <div
            data-legend-label={legendLabel}
            style={{ height: 0 }}
            aria-hidden="true"
          />
        );
      } else if (container.name === 'perspectiveBox') {
        return (
          <PerspectiveChoiceContent
            // eslint-disable-next-line react/no-children-prop
            children={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'tabBoxes') {
        return (
          <TabBoxes
            // eslint-disable-next-line react/no-children-prop
            children={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'branchpoint') {
        return (
          <BranchpointBox
            // eslint-disable-next-line react/no-children-prop
            children={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'branchSummary') {
        // Only meaningful inside :::branchpoint; render nothing if it leaks out.
        return null;
      } else if (container.name === 'gptScale') {
        return (
          <GPTScale />
        );
      } else if (container.name === 'corePrinciples') {
        return (
          <PlanPrinciplesBox
            // eslint-disable-next-line react/no-children-prop
            children={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'twoBoxes2029and2030') {
        return (
          <TwoBoxes2029and2030
            // eslint-disable-next-line react/no-children-prop
            children={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'keyMetrics2026') {
        return (
          <KeyMetrics2026 />
        );
      } else if (container.name === 'chinaComputeCentralization') {
        return (
          <ChinaComputeCentralization />
        );
      } else if (container.name === 'TakeoffMethod') {
        return (
          <TakeoffMethod />
        );
      } else if (container.name === 'WeightsSecurityTimeline') {
        return (
          <WeightsSecurityTimeline />
        );
      } else if (container.name === 'WeightsSecurityTimelineOriginal') {
        // Legacy directive name from when watermarked/unwatermarked variants
        // differed; now identical to WeightsSecurityTimeline.
        return (
          <WeightsSecurityTimeline />
        );
      } else if (container.name === 'SecretsSecurityTimeline') {
        return (
          <SecretsSecurityTimeline />
        );
      } else if (container.name === 'TimelinesMethod') {
        return (
          <TimelinesMethod />
        );
      } else if (container.name === 'ComputeAllocation2024v2027') {
        return (
          <ComputeAllocation2024v2027 />
        );
      } else if (container.name === 'DeploymentCurves') {
        return (
          <DeploymentCurves />
        );
      } else if (container.name === 'game') {
        const url = 'https://beat-the-black-projects.vercel.app/';
        const image = container.attributes?.image || '/game.jpg';
        const alt = container.attributes?.alt || 'Play the game';
        return <GameEmbed url={url} image={image} alt={alt} />;
      } else if (container.name === 'publicationDate') {
        return (
          <div className="hidden md:block">
            <PublicationDate />
          </div>
        );
      } else if (container.name === 'introLinks') {
        return <IntroLinkCards nodes={container.children} />;
      } else if (container.name === 'caption') {
        return (
          <div className="pl-2 pr-6 -mt-4 pb-[0.5px] font-['et-book',Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif] text-[#1a1a1a]">
            <Markdown
              className="[&_p]:text-[15.5px] [&_p]:leading-[1.45] [&_ol]:text-[14px] [&_ul]:text-[14px] [&_li]:text-[14px]"
              nodes={container.children}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
        );
      } else if (container.name === 'territory') {
        // Illustrated territory row: art on the left, its description
        // (markdown children, footnotes intact) on the right; stacks on
        // narrow screens.
        const kind = container.attributes?.kind as string | undefined;
        const art = kind ? TERRITORY_IMAGES[kind] : undefined;
        return (
          <div className="my-6 grid gap-5 md:grid-cols-[1.15fr_1fr] md:items-center">
            {art ? (
              <ZoomableImage src={art.src} fullSrc={art.full} alt={art.alt} className="w-full h-auto rounded-md" />
            ) : null}
            <div className="[&_p]:my-0 [&_p]:text-[1.05rem] [&_p]:leading-[1.5]">
              <Markdown nodes={container.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
            </div>
          </div>
        );
      } else if (container.name === 'chartEmbed') {
        const chartId = container.attributes?.chart as string | undefined;
        if (!chartId) return null;
        const explicitH = container.attributes?.height as string | undefined;
        const scenario = container.attributes?.scenario as string | undefined;
        const scenarioQ = scenario === 'default' ? '&scenario=default' : '';
        // Default: size the chart by its width (aspect-ratio) so it adjusts as the
        // column/viewport changes — clamped so it never gets too squat on mobile or
        // too tall on wide screens. An explicit `height=` opts into a fixed height.
        const sizeStyle: React.CSSProperties = explicitH
          ? { height: `${explicitH}px` }
          : { aspectRatio: '1.6', minHeight: '240px', maxHeight: '460px' };
        return (
          <div className="my-6">
            <iframe
              src={`/econ-explorer/index.html?embed=1&chart=${chartId}${scenarioQ}`}
              title={chartId}
              loading="lazy"
              style={{ width: '100%', ...sizeStyle, border: '1px solid #d9d7c8', borderRadius: '10px', background: '#fffef8' }}
            />
          </div>
        );
      } else if (container.name === 'box') {
        // `:::box` draws a bordered callout box around main-text content (the
        // treatment box-intent quotes used to get from the global blockquote
        // styling; `>` now renders as an ordinary left-rule quote). `md-box`
        // is the print.css hook: bordered boxes fragment badly across pages,
        // so print restyles these with the same left rule as blockquotes.
        return (
          <div className="md-box my-4 rounded-lg border-2 border-[#232323] px-6 py-5 [&>:first-child]:mt-0 [&>:last-child]:mb-0">
            <Markdown nodes={container.children} docContext={docContext} ending={ending} FootnoteHandler={FootnoteHandler} />
          </div>
        );
      } else if (container.name === 'modelbox') {
        const boxTitle = container.attributes?.title as string | undefined;
        return (
          <div className="my-7 rounded-lg border border-black/15 bg-[#fffef8] px-6 py-5 [&_p]:text-[0.93em] [&_p]:my-2 [&_.katex-display]:my-3">
            {boxTitle ? <div className="font-mono text-[15px] font-bold text-[#1a1a1a] mb-3">{boxTitle}</div> : null}
            <Markdown
              nodes={container.children}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
        );
      } else if (container.name === 'ai2027box') {
        // An excerpt embedded from ai-2027.com, framed as a print rendition of a
        // browser window: ink title bar with knockout window dots and the source
        // URL, then the quoted expandable box in AI 2027's own (sans) voice with
        // a green Slowdown-branch accent. Children are ordinary markdown, so
        // footnotes and links inside the quote keep working.
        const boxTitle = (container.attributes?.title as string) ?? 'Robot economy doubling times';
        const href = (container.attributes?.href as string) ?? 'https://ai-2027.com/slowdown#slowdown-2028-03-31';
        const context = (container.attributes?.context as string) ?? 'Slowdown ending · March 2028';
        return (
          <div className="not-prose my-8 border border-[#111111] bg-[#fffff8]">
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2.5 border-b border-[#111111] bg-[#111111] px-3.5 py-[7px] text-[#fffff8] no-underline hover:bg-[#333333]"
            >
              <span aria-hidden className="flex gap-[5px]">
                <span className="h-[9px] w-[9px] rounded-full border-[1.5px] border-[#fffff8]" />
                <span className="h-[9px] w-[9px] rounded-full border-[1.5px] border-[#fffff8]" />
                <span className="h-[9px] w-[9px] rounded-full border-[1.5px] border-[#fffff8]" />
              </span>
              <span className="font-['Menlo',Consolas,'Liberation_Mono','DejaVu_Sans_Mono',monospace] text-[12px] tracking-tight">
                ai-2027.com/slowdown
              </span>
              <span className="ml-auto font-['Menlo',Consolas,'Liberation_Mono','DejaVu_Sans_Mono',monospace] text-[11px] opacity-90">
                open ↗
              </span>
            </a>
            <div className="px-5 py-4 font-['Inter','Helvetica_Neue',Helvetica,Arial,sans-serif]">
              <details open className="group">
                <summary className="flex cursor-pointer list-none items-baseline gap-2.5 [&::-webkit-details-marker]:hidden">
                  <TriangleMarker className="inline-block translate-y-[-1px] text-[15px] text-[#2A623D] transition-transform duration-150 group-open:rotate-90" />
                  <span className="text-[15.5px] font-bold leading-snug text-[#1a1a1a]">{boxTitle}</span>
                  <span className="ml-auto whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-[#2A623D]">{context}</span>
                </summary>
                <div className="mt-3 border-l-2 border-[#2A623D]/30 pl-4 text-[#1a1a1a] [&_p]:my-2.5 [&_p]:text-[14px] [&_p]:leading-[1.55] [&_ul]:my-2.5 [&_ul]:pl-5 [&_li]:my-1.5 [&_li]:text-[14px] [&_li]:leading-[1.55] [&_a]:text-[#2A623D] [&_a]:underline">
                  <Markdown
                    nodes={container.children}
                    docContext={docContext}
                    FootnoteHandler={FootnoteHandler}
                  />
                </div>
              </details>
              <div className="mt-3 text-right text-[11px] italic text-[#1a1a1a] opacity-80">
                quoted from{' '}
                <a href={href} target="_blank" rel="noreferrer" className="text-[#2A623D] underline">
                  AI 2027
                </a>
                , the box as published in the Slowdown ending (March 2028)
              </div>
            </div>
          </div>
        );
      } else if (container.name === 'supplementExcerpt') {
        // A passage copied verbatim from another supplement on this site,
        // framed so the borrowed content reads as a quotation rather than part
        // of the host doc: an ink header bar naming (and linking to) the source
        // supplement, then the excerpt behind a red quote rule. Children are
        // ordinary markdown (headings, tables, figure directives all work), so
        // wrap with four colons when the excerpt itself contains ::: figures.
        const excerptTitle = (container.attributes?.title as string) ?? 'another supplement';
        const excerptHref = (container.attributes?.href as string) ?? '/supplements';
        return (
          <div className="my-8 border border-[#111111] bg-[#fffff8]">
            <a
              href={excerptHref}
              className="flex items-baseline gap-2 border-b border-[#111111] bg-[#111111] px-3.5 py-[7px] text-[#fffff8] no-underline hover:bg-[#333333]"
            >
              <span className="text-[12px] font-bold uppercase tracking-[0.08em]">Excerpt</span>
              <span className="text-[13px] italic">from the {excerptTitle} supplement</span>
              <span className="ml-auto whitespace-nowrap text-[12px]">open supplement →</span>
            </a>
            <div className="border-l-[3px] border-[#8b0000] px-5 py-1 my-3 ml-3.5 mr-1">
              <Markdown
                nodes={container.children}
                docContext={docContext}
                FootnoteHandler={FootnoteHandler}
              />
            </div>
            <div className="px-5 pb-3 text-right text-[12px] italic opacity-80">
              quoted from the{' '}
              <a href={excerptHref} className="text-[#8b0000] underline">
                {excerptTitle}
              </a>{' '}
              supplement
            </div>
          </div>
        );
      } else if (container.name === 'toc') {
        // In-content TOC; list markers dropped (text already says "Part 1.").
        return (
          <nav
            id="top-toc"
            aria-label="Table of contents"
            className={[
              'scroll-mt-8',
              'mt-10 pt-7 pb-7 border-t border-b border-black/20',
              '[&>ol]:list-none [&>ol]:pl-0 [&>ol]:m-0 [&>ol]:flex [&>ol]:flex-col [&>ol]:gap-6',
              '[&>ol>li]:m-0',
              '[&>ol>li>p]:m-0 [&>ol>li>p]:text-[1.6rem] [&>ol>li>p]:font-bold [&>ol>li>p]:leading-tight [&>ol>li>p]:tracking-tight',
              '[&_ol_ol]:list-none [&_ol_ol]:pl-0 [&_ol_ol]:m-0 [&_ol_ol]:mt-3 [&_ol_ol]:flex [&_ol_ol]:flex-col [&_ol_ol]:gap-2.5',
              '[&_ol_ol_li]:relative [&_ol_ol_li]:pl-7 [&_ol_ol_li]:m-0 [&_ol_ol_li]:text-[1.15rem] [&_ol_ol_li]:text-black/70',
              '[&_ol_ol_li]:before:content-["→"] [&_ol_ol_li]:before:absolute [&_ol_ol_li]:before:left-0 [&_ol_ol_li]:before:top-0 [&_ol_ol_li]:before:text-black/35',
              '[&_a]:no-underline [&_a]:text-inherit [&_a]:border-b [&_a]:border-black/15 [&_a]:pb-[1px] [&_a]:transition-colors [&_a]:duration-150',
              '[&_a:hover]:text-black [&_a:hover]:border-black/55',
            ].join(' ')}
          >
            <Markdown
              nodes={container.children}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </nav>
        );
      } else if (container.name === 'verificationTimeline') {
        return <VerificationPlanTimeline />;
      } else if (container.name === 'table') {
        // Tolerant pipe-table: gdoc spacing makes GFM see paragraphs, not a table.
        const rawChildren = (container.children as any[]) || [];
        const parsedRows: any[][][] = [];
        for (const child of rawChildren) {
          if (child.type !== 'paragraph') continue;
          const cells = splitInlineByPipe(child.children || []);
          if (cells.length > 0) parsedRows.push(cells);
        }
        if (parsedRows.length === 0) return null;

        // Find the separator row (cells like "---", ":---").
        let sepIdx = -1;
        for (let i = 0; i < parsedRows.length; i++) {
          const row = parsedRows[i];
          if (row.length === 0) continue;
          const allDashes = row.every((cell) => {
            const text = inlineText(cell).trim();
            return /^:?-{2,}:?$/.test(text);
          });
          if (allDashes) { sepIdx = i; break; }
        }
        const headerCells = sepIdx > 0 ? parsedRows[sepIdx - 1] : null;
        const bodyCells = parsedRows.filter((_, i) =>
          sepIdx === -1 ? true : (i !== sepIdx && i !== sepIdx - 1)
        );

        const cellCls = '!text-base !px-3 !py-2 align-top';
        const borderCls = 'border border-black';
        return (
          <table
            className="w-full my-6 border-collapse"
            style={{ fontSize: '1em' }}
          >
            {headerCells && (
              <thead>
                <tr>
                  {headerCells.map((cell, ci) => (
                    <th key={ci} className={`${borderCls} ${cellCls} text-left font-semibold`}>
                      <Markdown
                        nodes={cell as any}
                        docContext={docContext}
                        FootnoteHandler={FootnoteHandler}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {bodyCells.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={`${borderCls} ${cellCls} text-left`}>
                      <Markdown
                        nodes={cell as any}
                        docContext={docContext}
                        FootnoteHandler={FootnoteHandler}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      } else if (container.name === 'spacedList') {
        // Extra air between top-level items of a long multi-paragraph list,
        // so the item boundaries stay visible without full blank-line breaks.
        return (
          <div className="[&>ol>li+li]:mt-10 [&>ul>li+li]:mt-10">
            <Markdown
              nodes={container.children}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
        );
      } else if (container.name === 'biggerTables') {
        // Override tufte's 0.8em table font; its :where() is specificity-0, so plain selectors win.
        return (
          <div className="[&_table]:!text-base [&_th]:!px-3 [&_th]:!py-2 [&_td]:!px-3 [&_td]:!py-2">
            <Markdown
              nodes={container.children}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
        );
      } else if (container.name === 'prettytable') {
        // High-contrast nested table: internal hairlines only, bold label edges.
        // On phones the columns get crushed into one-word towers (or clipped),
        // so below md each body row renders as a stacked card instead: first
        // cell as the card title, remaining cells as header-labelled blocks.
        //
        // Optional table title: anything authored inside the fence ABOVE the
        // table renders as a bold title over both the desktop table and the
        // mobile cards. In the Google Doc that's just a line of text between
        // the `:::prettytable{compact}` line and the table. (A `title="…"`
        // directive attribute would be the obvious alternative, but Docs
        // smart-quotes the straight quotes when authors type it, which breaks
        // attribute parsing — plain content above the table round-trips.)
        const ptChildren = (container.children as any[]) || [];
        const tableIdx = ptChildren.findIndex((c) => c?.type === 'htmlTable');
        const tableNode = (tableIdx >= 0 ? ptChildren[tableIdx] : undefined) as HtmlTableNode | undefined;
        const titleNodes = tableIdx > 0 ? (ptChildren.slice(0, tableIdx) as Content[]) : [];
        const tableNodes = (tableIdx >= 0 ? ptChildren.slice(tableIdx) : ptChildren) as Content[];
        const plain = (children: Content[]) =>
          children.map(getTextFromNode).join(' ').replace(/\s+/g, ' ').trim();
        // Expand into a rowspan/colspan-expanded grid: spanned cells repeat
        // into every grid position they cover (fromSpan marks the copies), so
        // grouped tables (a rowspan label column) and two-row headers (a
        // colspan group header) can be laid out per-cell below.
        type GridCell = { children: Content[]; fromSpan: boolean; colspan: number };
        const grid: GridCell[][] = [];
        if (tableNode) {
          const hanging: Array<{ children: Content[]; rows: number } | undefined> = [];
          for (const row of tableNode.rows) {
            const out: GridCell[] = [];
            let col = 0;
            let k = 0;
            while (k < row.cells.length || (hanging[col] && hanging[col]!.rows > 0)) {
              const h = hanging[col];
              if (h && h.rows > 0) {
                out.push({ children: h.children, fromSpan: true, colspan: 1 });
                h.rows--;
                col++;
                continue;
              }
              const cell = row.cells[k++];
              if (!cell) break;
              for (let cc = 0; cc < cell.colspan; cc++) {
                out.push({ children: cell.children, fromSpan: cc > 0, colspan: cell.colspan });
                if (cell.rowspan > 1) hanging[col] = { children: cell.children, rows: cell.rowspan - 1 };
                col++;
              }
            }
            grid.push(out);
          }
        }
        // Two header rows when the first row carries a colspan group header
        // (e.g. "Amount of effective H100e" spanning the year columns): labels
        // come from the second row, suffixed with the group text.
        const twoRowHeader =
          grid.length > 2 && grid[0].some((c) => c.colspan > 1 && plain(c.children) !== '');
        const headerLabels = twoRowHeader
          ? grid[1].map((c, i) => {
              const label = plain(c.children);
              const group = grid[0][i] ? plain(grid[0][i].children) : '';
              const prevGroup = i > 0 && grid[0][i - 1] ? plain(grid[0][i - 1].children) : '';
              return group && group !== label && group !== prevGroup
                ? `${label} — ${group}`
                : label;
            })
          : (grid[0] || []).map((c) => plain(c.children));
        const bodyGrid = grid.slice(twoRowHeader ? 2 : 1);
        // A rowspan label column makes it a grouped table: one card per group
        // (rowspan run), with each leaf row a titled section inside the card.
        const grouped =
          bodyGrid.length > 0 && bodyGrid.some((r) => r[0] && r[0].fromSpan);
        type Leaf = { title: Content[]; cells: GridCell[] };
        type Group = { title: Content[]; leaves: Leaf[] };
        const groups: Group[] = [];
        if (grouped) {
          for (const r of bodyGrid) {
            if (!r[0]) continue;
            if (!r[0].fromSpan || groups.length === 0) {
              groups.push({ title: r[0].children, leaves: [] });
            }
            groups[groups.length - 1].leaves.push({ title: r[1] ? r[1].children : [], cells: r.slice(2) });
          }
        }
        // Short values render as one-line label/value rows; long ones stack.
        const valueBlock = (cell: GridCell, label: string, key: number) => {
          const text = plain(cell.children);
          if (label && text.length <= 24 && !cell.children.some((c) => c.type === 'list')) {
            return (
              <div key={key} className="mt-1.5 flex items-baseline justify-between gap-4">
                <div className="text-xs uppercase tracking-[0.06em] text-black/55">{label}</div>
                <Markdown
                  nodes={cell.children}
                  className="text-lg leading-snug text-right [&_p]:m-0 [&_p]:inline"
                  docContext={docContext}
                  FootnoteHandler={FootnoteHandler}
                />
              </div>
            );
          }
          return (
            <div key={key} className="mt-3">
              {label && (
                <div className="text-xs uppercase tracking-[0.06em] text-black/55 mb-0.5">
                  {label}
                </div>
              )}
              <Markdown
                nodes={cell.children}
                className="text-lg leading-snug [&_p]:m-0 [&_p+p]:mt-1 [&_ul]:my-1 [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:pl-5 [&_li]:mt-0.5"
                docContext={docContext}
                FootnoteHandler={FootnoteHandler}
              />
            </div>
          );
        };
        // {compact} keeps the same chrome at body-size text — for wide tables
        // whose prose columns turn into word-towers at text-xl.
        const isCompact = 'compact' in ((container.attributes as any) || {});
        // Authored title (content above the table inside the fence), shared by
        // the mobile-cards and desktop renders. Margins come from the render
        // sites (flex gap on mobile, mb on desktop), not from the title itself.
        const tableTitle =
          titleNodes.length > 0 ? (
            <div
              className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold leading-snug [&_p]:m-0 [&_p+p]:mt-1`}
            >
              <Markdown nodes={titleNodes} docContext={docContext} FootnoteHandler={FootnoteHandler} />
            </div>
          ) : null;
        const mobileCards =
          bodyGrid.length > 0 &&
          (grouped ? (
            <div className="md:hidden my-6 flex flex-col gap-4">
              {tableTitle}
              {groups.map((group, gi) => (
                <div key={gi} className="border-[1.5px] border-black px-4 py-3.5">
                  <Markdown
                    nodes={group.title}
                    className="font-semibold text-xl leading-snug [&_p]:m-0"
                    docContext={docContext}
                    FootnoteHandler={FootnoteHandler}
                  />
                  {group.leaves.map((leaf, li) => (
                    <div key={li} className="mt-3 border-t border-black/15 pt-2.5">
                      <Markdown
                        nodes={leaf.title}
                        className="text-lg leading-snug [&_p]:m-0 [&_strong]:font-semibold"
                        docContext={docContext}
                        FootnoteHandler={FootnoteHandler}
                      />
                      {leaf.cells.map((cell, ci) => valueBlock(cell, headerLabels[ci + 2] || '', ci))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="md:hidden my-6 flex flex-col gap-4">
              {tableTitle}
              {bodyGrid.map((row, ri) => {
                // A short first cell is a row label — render it as the card
                // title (with its column label above). A long one (a name plus
                // a whole description paragraph) renders as a normal labelled
                // block instead, so it doesn't blow up to title size.
                const firstCellText = plain(row[0] ? row[0].children : []);
                const titleFirst = firstCellText.length > 0 && firstCellText.length <= 70;
                const blocks = titleFirst ? row.slice(1) : row;
                const labelOffset = titleFirst ? 1 : 0;
                return (
                  <div key={ri} className="border-[1.5px] border-black px-4 py-3.5">
                    {titleFirst && (
                      <div>
                        {headerLabels[0] && (
                          <div className="text-xs uppercase tracking-[0.06em] text-black/55 mb-0.5">
                            {headerLabels[0]}
                          </div>
                        )}
                        <Markdown
                          nodes={row[0] ? row[0].children : []}
                          className="font-semibold text-xl leading-snug [&_p]:m-0 [&_p+p]:mt-1"
                          docContext={docContext}
                          FootnoteHandler={FootnoteHandler}
                        />
                      </div>
                    )}
                    {blocks.map((cell, ci) => valueBlock(cell, headerLabels[ci + labelOffset] || '', ci))}
                  </div>
                );
              })}
            </div>
          ));
        return (
          <>
            {mobileCards}
          <div
            className={[
              mobileCards ? 'hidden md:block' : '',
              'my-6',
              '[&_table]:!w-full [&_table]:!border-collapse [&_table]:!my-0',
              isCompact
                ? '[&_th]:!px-3 [&_th]:!py-2 [&_th]:!align-top [&_th]:!text-left [&_th]:!text-base [&_th]:!font-normal'
                : '[&_th]:!px-4 [&_th]:!py-3 [&_th]:!align-top [&_th]:!text-left [&_th]:!text-xl [&_th]:!font-normal',
              isCompact
                ? '[&_td]:!px-3 [&_td]:!py-2 [&_td]:!align-top [&_td]:!text-left [&_td]:!text-base [&_td]:!font-normal'
                : '[&_td]:!px-4 [&_td]:!py-3 [&_td]:!align-top [&_td]:!text-left [&_td]:!text-xl [&_td]:!font-normal',
              '[&_th]:!border-r-[3px] [&_th]:!border-b-[3px] [&_th]:!border-black [&_th]:!border-t-0 [&_th]:!border-l-0',
              '[&_td]:!border-r-[3px] [&_td]:!border-b-[3px] [&_td]:!border-black [&_td]:!border-t-0 [&_td]:!border-l-0',
              '[&_tr_>_*:last-child]:!border-r-0',
              // Grouped (rowspan) tables keep the bottom rule: a rowspan label
              // cell isn't in the last <tr>, so its bottom border can't be
              // stripped with it — keep the whole bottom edge instead of
              // leaving a lone black segment under the label column.
              grouped
                ? '[&_thead_tr:last-child_>_*]:!border-b-0'
                : '[&_thead_tr:last-child_>_*]:!border-b-0 [&_tbody_tr:last-child_>_*]:!border-b-0',
              '[&_thead_tr_>_*]:!font-semibold',
              '[&_tr_>_*:first-child]:!font-semibold',
              // No-<thead> fallback: bold the first tbody row too.
              '[&_table:not(:has(thead))_tbody_tr:first-child_>_*]:!font-semibold',
              // strong/b inherit cell weight so **bold** doesn't force 700 in interior cells.
              '[&_strong]:!font-[inherit] [&_b]:!font-[inherit]',
            ].join(' ')}
          >
            {tableTitle && <div className="mb-2.5">{tableTitle}</div>}
            <Markdown
              nodes={tableNodes}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
          </>
        );
      } else if (container.name === 'flowtable') {
        // Compact table matching WorldComputeBasicFlow.
        return (
          <div
            className={[
              'my-4 overflow-x-auto font-serif',
              '[&_table]:!w-full [&_table]:!my-0 [&_table]:!border-collapse [&_table]:!border [&_table]:!border-gray-300',
              '[&_th]:!bg-gray-100 [&_th]:!px-2.5 [&_th]:!py-1.5 [&_th]:!text-right [&_th]:!align-bottom [&_th]:!text-xs [&_th]:!font-bold',
              '[&_th]:!border-0 [&_th]:!border-b [&_th]:!border-black',
              '[&_td]:!px-2.5 [&_td]:!py-1 [&_td]:!text-right [&_td]:!align-top [&_td]:!text-[13px] [&_td]:!border-0',
              '[&_tr_>_*:first-child]:!text-left [&_tr_>_*:first-child]:!font-semibold',
            ].join(' ')}
          >
            <Markdown
              nodes={container.children}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
        );
      } else if (container.name === 'policyList') {
        return (
          <IncrementalistWishlistDiagram
            // eslint-disable-next-line react/no-children-prop
            children={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'recsNested') {
        return (
          <RecsNested
            nodes={container.children}
            docContext={docContext}
            FootnoteHandler={FootnoteHandler}
          />
        );
      } else if (container.name === 'recs') {
        // Recommendation list: bold headline rec + muted nested reasoning.
        return (
          <div
            className={[
              'my-7',
              '[&>ol]:list-none [&>ol]:pl-0 [&>ol]:m-0 [&>ol]:flex [&>ol]:flex-col [&>ol]:gap-7',
              '[&>ol>li]:relative [&>ol>li]:m-0 [&>ol>li]:pl-5 [&>ol>li]:border-l-[3px] [&>ol>li]:border-black/80',
              '[&>ol>li>p]:m-0 [&>ol>li>p]:text-[1.4rem] [&>ol>li>p]:font-bold [&>ol>li>p]:leading-tight [&>ol>li>p]:text-black',
              '[&_ol_ol]:list-none [&_ol_ol]:pl-0 [&_ol_ol]:m-0 [&_ol_ol]:mt-2 [&_ol_ol]:flex [&_ol_ol]:flex-col [&_ol_ol]:gap-1.5',
              '[&_ol_ol_li]:m-0 [&_ol_ol_li]:!text-[1.05rem] [&_ol_ol_li]:font-normal [&_ol_ol_li]:text-black/60 [&_ol_ol_li]:!leading-[1.4]',
              '[&_ol_ol_li>p]:m-0 [&_ol_ol_li>p]:!text-[1.05rem] [&_ol_ol_li>p]:!leading-[1.4]',
            ].join(' ')}
          >
            <Markdown
              nodes={container.children}
              docContext={docContext}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
        );
      } else if (container.name === 'summary') {
        // Used to show preview of supplement on supplements page
        return <></>;
      } else if (container.name === 'lg' || container.name === 'xl') {
        // Breakpoint-gated content: children render only at the named
        // Tailwind breakpoint and up (lg ≥1024px, xl ≥1280px). CSS-only so
        // SSR output is deterministic — the content is in the DOM at every
        // width, just hidden below the cutoff.
        return (
          <div className={container.name === 'xl' ? 'hidden xl:block' : 'hidden lg:block'}>
            <Markdown
              nodes={container.children}
              docContext={docContext}
              ending={ending}
              FootnoteHandler={FootnoteHandler}
            />
          </div>
        );
      } else if (container.name === 'timelineQuestions') {
        // "The Challenges Ahead" timeline. Unlike the static figures below, it
        // takes an `open="…"` attribute that lists which subsections start
        // expanded — 1-indexed, comma-separated (e.g. `open="1,3"`). Omit for
        // all-collapsed. Parse it here and pass through as `openSections`.
        const openAttr = (container.attributes?.open as string | undefined) || '';
        const openSections = openAttr
          .split(',')
          .map((n: string) => parseInt(n.trim(), 10))
          .filter((n: number) => Number.isFinite(n));
        return (
          <div className={[
            'my-6',
            '[&>div]:!px-0 [&>div]:!py-0 [&>div]:!max-w-none',
            '[&_h1]:!leading-snug [&_h2]:!leading-snug [&_p]:!leading-snug',
            '[&_h1]:!mb-1 [&_h2]:!mb-1',
          ].join(' ')}>
            <TimelineQuestionsDiagram openSections={openSections} />
          </div>
        );
      } else if (container.name === 'PolicyScatter') {
        // Interactive plausibility × desirability scatter. Unlike the static
        // figures below it is data-driven: the policies come from the
        // Google-Doc table wrapped inside the directive (heading row, then
        // [policy, plausibility 0–1, desirability 0–1, explanation] rows),
        // which the importer keeps as a raw-HTML child — same mechanism as
        // `:::prettytable` above. Accepts `{title="…"}` to override the
        // attribution-line figure title.
        const rawTableHtml = ((container.children as any[]) || [])
          .map((c) => (c?.type === 'htmlTable' ? String(c.rawHtml || '') : c?.type === 'html' ? String(c.value || '') : ''))
          .find((v) => /<table[\s>]/i.test(v));
        if (!rawTableHtml) return <></>;
        return (
          <PolicyScatterChart
            tableHtml={rawTableHtml}
            title={(container.attributes?.title as string | undefined) || undefined}
          />
        );
      } else if (container.name && FIGURES_DIRECTIVES[container.name]) {
        // Figures in app/figures-code/ wrap themselves in a card-style outer
        // div (e.g. `max-w-[1100px] mx-auto px-12 py-16`) tuned for the
        // standalone /figures-code page. Inside a narrative chunk that
        // padding swamps the figure, so neutralize it on the root child and
        // let this wrapper own the narrative spacing instead.
        // (Figures carry their own attribution styling now — the old
        // corner-badge watermark logic is gone; a `{nowatermark}` attribute
        // is accepted but has no effect.)
        const figure = FIGURES_DIRECTIVES[container.name];
        return (
          <div className={FIGURE_IN_PROSE_CLASSES}>
            {figure}
          </div>
        );
      }
      break;
    }
    case 'details': {
      const detailsNode = node as any;
      const summaryLines: string[] = (detailsNode.summaryLines && detailsNode.summaryLines.length > 0)
        ? detailsNode.summaryLines
        : [detailsNode.summary];
      const summaryLineNodes = detailsNode.summaryLineNodes;
      // Slug of the summary, used as a stable scroll/expand target. Kept on the
      // <details> element itself (not the inner content div) so the deep-link
      // machinery can toggle `.open` on the element it finds by id.
      const detailsSlug = slugify(detailsNode.summary, { lower: true, strict: true });
      return (
        <details
          id={detailsSlug}
          // A page loaded at #<slug> starts with this section open + scrolled to.
          ref={(el) => openDetailsIfHashTarget(el, detailsSlug)}
          // Reflect open/close into the URL hash so an expanded section is
          // shareable / deep-linkable (see detailsHashSync for the rules).
          onToggle={(e) => syncDetailsHash(detailsSlug, (e.currentTarget as HTMLDetailsElement).open)}
          className="group my-3 scroll-mt-20"
        >
          <summary className="flex list-none items-center justify-start pl-1 py-0.5 leading-snug italic transition-colors [&::-webkit-details-marker]:hidden">
            {/* h matches leading-snug (1.375) so the chevron centers on the first line of the title. */}
            {/* Outer box centers on the first text line; the marker's own transform is rotation-only so it stays clean when opened. Vertical optical centering of the triangle is handled by the flex alignment. */}
            <span className="mr-2 flex h-[1.375em] shrink-0 items-center" aria-hidden="true">
              <TriangleMarker className="block text-[#8b0000] transition-transform duration-150 group-open:rotate-90" />
            </span>
            <span>
              {summaryLineNodes
                ? summaryLineNodes.map((summaryLine: Content[], i: number) => (
                  <span className={`block ${i > 0 ? 'opacity-80 [&_em]:text-[0.9em]' : ''}`} key={i}>
                    <Markdown nodes={summaryLine} docContext={docContext} FootnoteHandler={FootnoteHandler} />
                  </span>
                ))
                : summaryLines.map((summaryLine, i) => (
                  <span className={`block ${i > 0 ? 'opacity-80' : ''}`} key={i}>{summaryLine}</span>
                ))}
            </span>
          </summary>
          <div className="mt-4 mb-4 ml-2 pr-2 border-l border-[#8b0000] p-3 px-7 text-[0.92em] leading-snug [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <Markdown nodes={detailsNode.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
          </div>
        </details>
      );
    }
    // Raw-HTML tables, restructured into mdast by remarkHtmlTables: cells
    // render through the normal pipeline, so footnotes (FootnoteRef with
    // tooltip + modal), links, and emphasis inside tables behave exactly
    // like in body prose.
    case 'htmlTable': {
      const table = node as unknown as HtmlTableNode;
      return (
        <div className="overflow-x-auto [&_table]:text-[0.8em]">
          <table>
            <tbody>
              {table.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.cells.map((cell, ci) => {
                    const CellTag = cell.header ? 'th' : 'td';
                    return (
                      <CellTag
                        key={ci}
                        colSpan={cell.colspan > 1 ? cell.colspan : undefined}
                        rowSpan={cell.rowspan > 1 ? cell.rowspan : undefined}
                        // Whitelisted styling carried over from the source
                        // cell (number-column alignment, heatmap shading).
                        style={cell.style}
                      >
                        <Markdown nodes={cell.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
                      </CellTag>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    // Fallback for raw HTML that remarkHtmlTables couldn't restructure.
    case 'html': {
      const htmlNode = node as any;
      const value = (htmlNode.value || '').trim();
      // Real <br />, not a block <div>, to avoid extra vertical spacing in table cells.
      if (/^<br\s*\/?\s*>$/i.test(value)) {
        return <br />;
      }
      // Degraded footnote handling only (static links, no tooltip): [^N]
      // markers are literal text inside raw HTML.
      const prefix = docContext?.prefix;
      const htmlWithFootnotes = (htmlNode.value as string).replace(
        /\[\^(\d+)\]/g,
        (_match, num: string) => {
          const identifier = prefix ? `${prefix}-${num}` : num;
          return `<sup id="footnote-${identifier}" class="relative text-[var(--accent)]"><a href="/footnotes#footnote-${identifier}" class="text-[var(--accent)] text-xs no-underline">${num}</a></sup>`;
        }
      );
      return <div className="overflow-x-auto [&_table]:text-[0.8em]" dangerouslySetInnerHTML={{ __html: htmlWithFootnotes }} />;
    }
    case 'inlineMath': {
      const inlineMath = node as any;
      return <KatexMath value={unescapeMarkdownForLaTeX(inlineMath.value)} />;
    }
    case 'math': {
      const math = node as any;
      return <KatexMath value={unescapeMarkdownForLaTeX(math.value)} displayMode />;
    }
    case 'code': {
      const codeNode = node as any;
      return (
        <pre
          className="my-4 overflow-x-auto rounded bg-gray-100 p-3 text-[13px] leading-relaxed"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
        >
          <code>{codeNode.value}</code>
        </pre>
      );
    }
    case 'inlineCode': {
      const inlineCode = node as any;
      return (
        <code
          className="rounded bg-gray-100 px-[0.3em] py-[0.1em] text-[0.88em]"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
        >
          {inlineCode.value}
        </code>
      );
    }
    default: {
      const parent = node as Parent;
      if (parent.children) {
        return (
          <div>
            <Markdown nodes={parent.children} docContext={docContext} FootnoteHandler={FootnoteHandler} />
          </div>
        );
      }
      return null;
    }
  }
}
